import type { Router } from 'vue-router';
import type { MagicLinkExchangeResult } from '@neon-one/auctions-types';

import { useSession } from '@/shell/stores/session';
import { useAuctionEvent, ShellRoute } from '@/shell/stores/auctionEvent';
import { httpClient } from '@/shell/http';
import { openActivationGate } from '@/shell/activation-bus';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresActivation?: boolean;
    skipEventLoad?: boolean;
    hideBottomNav?: boolean;
    hideSideNav?: boolean;
    autoHideHeader?: boolean;
  }
}

/**
 * rootGuards installs the bidder-shell guard skeleton:
 *   eventLoaded     — hydrates auctionEvent store from :slug param
 *   requireAuth     — redirects to /:slug/sign-in when no JWT
 *   requireActivated— opens the activation gate when bidder isn't activated
 *
 * Each guard reads route.meta to know whether to fire. fe-004 / fe-005 /
 * fe-006 set the meta flags on their feature routes; the shell does not
 * own the policy decision, only the enforcement.
 */
export function rootGuards(router: Router): void {
    useAuctionEvent().setNotFoundHandler(() => router.replace({ name: ShellRoute.NotFound }));
  router.beforeEach(async (to) => {
    // 1) eventLoaded — hydrate the AuctionEvent store before children render.
    if (to.params.slug && typeof to.params.slug === 'string' && !to.meta.skipEventLoad) {
      const auctionEvent = useAuctionEvent();
      try {
        await auctionEvent.loadBySlug(to.params.slug);
      } catch {
        // B-S1: event-not-found falls through to the not-found route. The
        // not-found view reads auctionEvent.error to show the right copy.
        if (to.name !== ShellRoute.NotFound) {
          return { name: ShellRoute.NotFound };
        }
      }

      const session = useSession();
      if (session.isAuthenticated && session.eventSlug !== to.params.slug) {
        session.clear();
      }
    }

    // 2) requireAuth — if a ?token= is present, exchange it inline so
    //    email links (checkout, offers) land directly on the target page
    //    without a sign-in intermediary.
    if (to.meta.requiresAuth && !useSession().isAuthenticated) {
      const token = to.query.token as string | undefined;
      let tokenExpired = false;
      if (token) {
        try {
          const { data } = await httpClient().post<MagicLinkExchangeResult>(
            '/magic-links/exchange',
            { token },
          );
          if (data.kind === 'authenticated') {
            // The grant names its own event (GT1-546). Reading it off the URL
            // instead would let a token minted for one event open a session
            // stamped with another's slug, which the slug-change check below
            // then reads as a mismatch and clears.
            useSession().adoptRegistration({ jwt: data.jwt, bidder: data.bidder, eventSlug: data.eventSlug });
            const query = { ...to.query };
            delete query.token;
            return { path: to.path, query, replace: true };
          }
        } catch {
          tokenExpired = true;
        }
      }
      const slug = to.params.slug ?? useAuctionEvent().slug;
      if (!slug) return '/';
      const cleanQuery = { ...to.query };
      delete cleanQuery.token;
      const cleanPath = to.path + (Object.keys(cleanQuery).length
        ? '?' + new URLSearchParams(cleanQuery as Record<string, string>).toString()
        : '');
      const params = new URLSearchParams({ next: cleanPath });
      if (tokenExpired) params.set('reason', 'link_expired');
      return `/${slug as string}/sign-in?${params.toString()}`;
    }

    // 3) requireActivated — open the activation gate (modal lives in the
    //    activation feature; the shell only triggers via the bus).
    if (to.meta.requiresActivation && !useSession().isActivated) {
      const activated = await openActivationGate();
      if (!activated) return false;
    }

    return true;
  });
}
